import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { axiosTokenInstance } from '../../utils/axiosInstance.utils';
import {
  apiUrls,
  errorMessages,
  paymentResultMessages,
  routeURL,
} from '../../utils/properties.utils';

export default function PaymentCancel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = StyledSnackbar();
  const [isRetrying, setIsRetrying] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updateCancelledCheckout = useCallback(async () => {
    try {
      const payhereReturnPayload = Object.fromEntries(searchParams.entries());

      await axiosTokenInstance.post(
        apiUrls.SUBSCRIPTION_CHECKOUT_CANCEL,
        payhereReturnPayload
      );
    } catch (error) {
      console.error(paymentResultMessages.CANCEL_STATUS_UPDATE_FAILED, error);
    }
  }, [searchParams]);

  useEffect(() => {
    updateCancelledCheckout();
  }, [updateCancelledCheckout]);

  const submitPayhereCheckout = (
    checkoutUrl: string,
    payload: Record<string, unknown>
  ) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = checkoutUrl;

    Object.entries(payload).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value ?? '');
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  };

  const handleRetryPayment = async () => {
    let checkoutSubmitted = false;

    try {
      setIsRetrying(true);

      const statusResponse = await axiosTokenInstance.get(
        apiUrls.SUBSCRIPTION_STATUS
      );
      const paidUserCount = Number(
        statusResponse.data?.data?.paidUserCount || 1
      );

      const response = await axiosTokenInstance.post(
        apiUrls.SUBSCRIPTION_CHECKOUT,
        { paidUserCount }
      );

      const checkoutUrl = response.data?.data?.checkoutUrl;
      const payload = response.data?.data?.checkoutPayload;

      if (!checkoutUrl || !payload) {
        throw new Error(paymentResultMessages.INVALID_CHECKOUT_RESPONSE);
      }

      submitPayhereCheckout(checkoutUrl, payload);
      checkoutSubmitted = true;
    } catch (error) {
      console.error(paymentResultMessages.RETRY_PAYMENT_FAILED, error);
      const errorMessage = await getApiErrorMessage(error);

      if (isMountedRef.current) {
        showSnackbar(
          errorMessage || errorMessages.SOMETHING_WENT_WRONG,
          'error'
        );
      }
    } finally {
      if (!checkoutSubmitted && isMountedRef.current) {
        setIsRetrying(false);
      }
    }
  };

  const handleBackToSite = async () => {
    setIsReturning(true);
    await updateCancelledCheckout();
    navigate(routeURL.USER + routeURL.SETTINGS, {
      state: { selectedTab: 2 },
    });
  };

  return (
    <Box sx={{ p: 5 }}>
      <Typography variant="h4" color="error">
        {paymentResultMessages.CANCEL_TITLE}
      </Typography>

      <Typography sx={{ mt: 2 }}>
        {paymentResultMessages.CANCEL_DESCRIPTION}
      </Typography>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleRetryPayment}
          disabled={isRetrying || isReturning}
          startIcon={isRetrying ? <CircularProgress size={16} /> : null}
        >
          {isRetrying
            ? paymentResultMessages.REDIRECTING_ACTION
            : paymentResultMessages.TRY_AGAIN_ACTION}
        </Button>

        <Button
          variant="outlined"
          onClick={handleBackToSite}
          disabled={isRetrying || isReturning}
          startIcon={isReturning ? <CircularProgress size={16} /> : null}
        >
          {isReturning
            ? paymentResultMessages.REDIRECTING_ACTION
            : paymentResultMessages.BACK_TO_SITE_ACTION}
        </Button>
      </Box>
    </Box>
  );
}
